import { useState, useEffect, useCallback } from "react";
import { Loader2, UserPlus, Trash2, KeyRound, X, Copy, Check } from "lucide-react";
import { api } from "../../lib/api";

function genPassword() {
  const chars = "abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let out = "";
  const rnd = new Uint32Array(12);
  (window.crypto || {}).getRandomValues?.(rnd);
  for (let i = 0; i < 12; i++) out += chars[(rnd[i] || Math.floor(Math.random() * 1e9)) % chars.length];
  return out;
}

export default function UsersPanel({ me }) {
  const [users, setUsers] = useState(null);
  const [msg, setMsg] = useState(null); // {ok, text}
  const [busy, setBusy] = useState(false);

  // add-user form
  const [showAdd, setShowAdd] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newPass, setNewPass] = useState("");

  // reset-password inline
  const [resetFor, setResetFor] = useState(null);
  const [resetPass, setResetPass] = useState("");

  const [copied, setCopied] = useState("");

  const load = useCallback(() => {
    api.listUsers().then(setUsers).catch((e) => setMsg({ ok: false, text: e.message }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const copy = (text, id) => {
    navigator.clipboard?.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(""), 1500);
  };

  const startAdd = () => { setNewEmail(""); setNewPass(genPassword()); setShowAdd(true); setMsg(null); };

  const addUser = async () => {
    setBusy(true); setMsg(null);
    try {
      await api.createUser(newEmail.trim(), newPass);
      setMsg({ ok: true, text: `User ${newEmail.trim().toLowerCase()} created. Share the temporary password below — they can change it after signing in.` });
      setShowAdd(false);
      load();
    } catch (e) {
      setMsg({ ok: false, text: e.message });
    } finally { setBusy(false); }
  };

  const doReset = async (email) => {
    setBusy(true); setMsg(null);
    try {
      await api.resetUserPassword(email, resetPass);
      setMsg({ ok: true, text: `Password reset for ${email}. Share the new temporary password.` });
      setResetFor(null);
      load();
    } catch (e) {
      setMsg({ ok: false, text: e.message });
    } finally { setBusy(false); }
  };

  const remove = async (email) => {
    if (!window.confirm(`Remove access for ${email}?`)) return;
    setMsg(null);
    try { await api.deleteUser(email); load(); }
    catch (e) { setMsg({ ok: false, text: e.message }); }
  };

  if (users === null) {
    return <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-[#00F0FF]" size={26} /></div>;
  }

  return (
    <div className="max-w-2xl">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-white font-bold">Team access</h3>
          <p className="text-slate-500 text-xs mt-0.5">Everyone listed here has full admin access to the editor.</p>
        </div>
        <button onClick={startAdd} className="cyber-btn-primary py-2 px-4 flex items-center gap-2 text-sm">
          <UserPlus size={15} /> Add user
        </button>
      </div>

      {msg && (
        <div className={`text-xs mb-4 rounded p-3 border ${msg.ok ? "text-green-300 bg-green-400/10 border-green-400/20" : "text-red-300 bg-red-400/10 border-red-400/20"}`}>
          {msg.text}
        </div>
      )}

      {/* Add user form */}
      {showAdd && (
        <div className="cyber-card p-5 mb-5">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-white font-semibold text-sm">New user</h4>
            <button onClick={() => setShowAdd(false)} className="text-slate-400 hover:text-white"><X size={16} /></button>
          </div>
          <label className="block text-[11px] uppercase tracking-wide text-slate-500 mb-1">Email</label>
          <input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="name@bpgalbania.com"
            className="w-full mb-4 bg-[#0A0F2E] border border-white/10 focus:border-[#00F0FF]/50 rounded px-3 py-2 text-sm text-slate-100 outline-none" />
          <label className="block text-[11px] uppercase tracking-wide text-slate-500 mb-1">Temporary password</label>
          <div className="flex gap-2 mb-4">
            <input type="text" value={newPass} onChange={(e) => setNewPass(e.target.value)}
              className="flex-1 bg-[#0A0F2E] border border-white/10 focus:border-[#00F0FF]/50 rounded px-3 py-2 text-sm text-slate-100 outline-none font-mono" />
            <button onClick={() => copy(newPass, "new")} className="px-3 border border-white/10 rounded text-slate-300 hover:text-[#00F0FF]" title="Copy">
              {copied === "new" ? <Check size={15} /> : <Copy size={15} />}
            </button>
            <button onClick={() => setNewPass(genPassword())} className="px-3 border border-white/10 rounded text-slate-300 hover:text-[#00F0FF] text-xs">New</button>
          </div>
          <button onClick={addUser} disabled={busy} className="cyber-btn-primary py-2 px-5 flex items-center gap-2 text-sm">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <UserPlus size={15} />} Create user
          </button>
        </div>
      )}

      {/* Users list */}
      <div className="space-y-2">
        {users.map((u) => (
          <div key={u.email} className="cyber-card p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-white text-sm truncate">
                  {u.email}
                  {u.email === me.email && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-[#00F0FF]/10 text-[#00F0FF]">you</span>}
                  {u.must_change_password && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-amber-400/10 text-amber-300">temp password</span>}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => { setResetFor(resetFor === u.email ? null : u.email); setResetPass(genPassword()); }}
                  className="p-2 text-slate-400 hover:text-[#00F0FF]" title="Reset password"><KeyRound size={15} /></button>
                {u.email !== me.email && (
                  <button onClick={() => remove(u.email)} className="p-2 text-slate-400 hover:text-red-400" title="Remove"><Trash2 size={15} /></button>
                )}
              </div>
            </div>

            {resetFor === u.email && (
              <div className="mt-3 pt-3 border-t border-white/5 flex flex-wrap gap-2 items-end">
                <div className="flex-1 min-w-[180px]">
                  <label className="block text-[11px] uppercase tracking-wide text-slate-500 mb-1">New temporary password</label>
                  <input type="text" value={resetPass} onChange={(e) => setResetPass(e.target.value)}
                    className="w-full bg-[#0A0F2E] border border-white/10 rounded px-3 py-2 text-sm text-slate-100 outline-none font-mono" />
                </div>
                <button onClick={() => copy(resetPass, "reset")} className="px-3 py-2 border border-white/10 rounded text-slate-300 hover:text-[#00F0FF]" title="Copy">
                  {copied === "reset" ? <Check size={15} /> : <Copy size={15} />}
                </button>
                <button onClick={() => doReset(u.email)} disabled={busy} className="cyber-btn-primary py-2 px-4 text-sm flex items-center gap-2">
                  {busy ? <Loader2 size={14} className="animate-spin" /> : "Set password"}
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
